import React, { useState, useEffect } from 'react';
import { 
  Server, 
  Database, 
  Shield, 
  Globe, 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Wifi,
  Lock,
  Network,
  Zap,
  Users,
  BarChart3,
  TrendingUp,
  RefreshCw,
  Settings,
  Info
} from 'lucide-react';
import DataMetricsDisplay from './DataMetricsDisplay';

interface ServiceStatus {
  name: string;
  status: 'operational' | 'degraded' | 'outage' | 'maintenance';
  uptime: string;
  responseTime: number;
  lastIncident?: string;
  description: string;
}

interface NetworkMetrics {
  totalRequests: number;
  averageResponseTime: number;
  errorRate: number;
  activeConnections: number;
  dataTransferred: string;
}

interface SecurityMetrics {
  encryptionStatus: 'active' | 'warning' | 'error';
  lastSecurityScan: string;
  vulnerabilitiesFound: number;
  complianceStatus: 'compliant' | 'review' | 'non-compliant';
  auditLogs: number;
}

export default function SystemStatusPage() {
  const [services, setServices] = useState<ServiceStatus[]>([
    {
      name: 'Main Database',
      status: 'operational',
      uptime: '99.98%',
      responseTime: 45,
      description: 'Primary health data storage and retrieval'
    },
    {
      name: 'Authentication Service',
      status: 'operational',
      uptime: '99.99%',
      responseTime: 23,
      description: 'User authentication and session management'
    },
    {
      name: 'Data Sync Engine',
      status: 'operational',
      uptime: '99.95%',
      responseTime: 67,
      description: 'Real-time data synchronization'
    },
    {
      name: 'AI Health Coach',
      status: 'operational',
      uptime: '99.92%',
      responseTime: 89,
      description: 'AI-powered health insights and recommendations'
    },
    {
      name: 'P2P Network',
      status: 'operational',
      uptime: '99.89%',
      responseTime: 156,
      description: 'Decentralized data sharing network'
    },
    {
      name: 'Analytics Engine',
      status: 'operational',
      uptime: '99.94%',
      responseTime: 78,
      description: 'Health analytics and reporting'
    }
  ]);

  const [networkMetrics, setNetworkMetrics] = useState<NetworkMetrics>({
    totalRequests: 0,
    averageResponseTime: 0,
    errorRate: 0,
    activeConnections: 0,
    dataTransferred: '0 MB'
  });

  const [securityMetrics, setSecurityMetrics] = useState<SecurityMetrics>({
    encryptionStatus: 'active',
    lastSecurityScan: '2 hours ago',
    vulnerabilitiesFound: 0,
    complianceStatus: 'compliant',
    auditLogs: 0
  });

  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Simulate real-time updates
  useEffect(() => {
    const updateMetrics = () => {
      // Update network metrics
      setNetworkMetrics({
        totalRequests: Math.floor(Math.random() * 1000000) + 5000000,
        averageResponseTime: Math.floor(Math.random() * 50) + 30,
        errorRate: Math.random() * 0.1,
        activeConnections: Math.floor(Math.random() * 500) + 1000,
        dataTransferred: `${(Math.random() * 100 + 50).toFixed(1)} GB`
      });

      // Update security metrics
      setSecurityMetrics({
        encryptionStatus: 'active' as const,
        lastSecurityScan: '2 hours ago',
        vulnerabilitiesFound: Math.floor(Math.random() * 3),
        complianceStatus: 'compliant' as const,
        auditLogs: Math.floor(Math.random() * 10000) + 50000
      });

      // Occasionally simulate service issues
      setServices(prev => prev.map(service => {
        if (Math.random() < 0.05) {
          return {
            ...service,
            status: 'degraded' as const,
            responseTime: service.responseTime * 1.5
          };
        }
        return service;
      }));

      setLastUpdated(new Date());
    };

    // Initial update
    updateMetrics();

    // Update every 30 seconds
    const interval = setInterval(updateMetrics, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational': return 'text-green-600 bg-green-50 border-green-200';
      case 'degraded': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'outage': return 'text-red-600 bg-red-50 border-red-200';
      case 'maintenance': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational': return <CheckCircle className="h-4 w-4" />;
      case 'degraded': return <AlertTriangle className="h-4 w-4" />;
      case 'outage': return <AlertTriangle className="h-4 w-4" />;
      case 'maintenance': return <Settings className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">System Status</h2>
          <p className="text-slate-600 mt-1">
            Real-time monitoring of our health data infrastructure
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
        </button>
      </div>

      {/* Last Updated */}
      <div className="text-sm text-slate-500">
        Last updated: {lastUpdated.toLocaleTimeString()}
      </div>

      {/* Data Metrics Display */}
      <DataMetricsDisplay />

      {/* Service Status */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Service Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {services.map((service, index) => (
            <div key={index} className={`p-4 rounded-lg border ${getStatusColor(service.status)}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  {getStatusIcon(service.status)}
                  <span className="font-medium">{service.name}</span>
                </div>
                <span className="text-sm font-medium capitalize">{service.status}</span>
              </div>
              <p className="text-sm text-slate-600 mb-3">{service.description}</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-500">Uptime:</span>
                  <span className="font-medium ml-1">{service.uptime}</span>
                </div>
                <div>
                  <span className="text-slate-500">Response:</span>
                  <span className="font-medium ml-1">{service.responseTime}ms</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Network Metrics */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Network Performance</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <Wifi className="h-6 w-6 text-blue-600 mx-auto mb-2" />
            <p className="text-xl font-bold text-blue-600">{formatNumber(networkMetrics.totalRequests)}</p>
            <p className="text-sm text-blue-700">Total Requests</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <Activity className="h-6 w-6 text-green-600 mx-auto mb-2" />
            <p className="text-xl font-bold text-green-600">{networkMetrics.averageResponseTime}ms</p>
            <p className="text-sm text-green-700">Avg Response</p>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <AlertTriangle className="h-6 w-6 text-orange-600 mx-auto mb-2" />
            <p className="text-xl font-bold text-orange-600">{(networkMetrics.errorRate * 100).toFixed(2)}%</p>
            <p className="text-sm text-orange-700">Error Rate</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <Network className="h-6 w-6 text-purple-600 mx-auto mb-2" />
            <p className="text-xl font-bold text-purple-600">{formatNumber(networkMetrics.activeConnections)}</p>
            <p className="text-sm text-purple-700">Active Connections</p>
          </div>
          <div className="text-center p-4 bg-indigo-50 rounded-lg">
            <BarChart3 className="h-6 w-6 text-indigo-600 mx-auto mb-2" />
            <p className="text-xl font-bold text-indigo-600">{networkMetrics.dataTransferred}</p>
            <p className="text-sm text-indigo-700">Data Transferred</p>
          </div>
        </div>
      </div>

      {/* Security Metrics */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Security & Compliance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="font-medium text-slate-700 mb-3">Security Status</h5>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Encryption Status</span>
                <div className="flex items-center space-x-1">
                  <Shield className="h-4 w-4 text-green-600" />
                  <span className="font-medium capitalize">{securityMetrics.encryptionStatus}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Last Security Scan</span>
                <span className="font-medium">{securityMetrics.lastSecurityScan}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Vulnerabilities Found</span>
                <span className="font-medium">{securityMetrics.vulnerabilitiesFound}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Compliance Status</span>
                <span className="font-medium capitalize">{securityMetrics.complianceStatus}</span>
              </div>
            </div>
          </div>
          <div>
            <h5 className="font-medium text-slate-700 mb-3">Audit & Monitoring</h5>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Audit Logs</span>
                <span className="font-medium">{formatNumber(securityMetrics.auditLogs)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">HIPAA Compliance</span>
                <div className="flex items-center space-x-1">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="font-medium">Compliant</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Data Encryption</span>
                <div className="flex items-center space-x-1">
                  <Lock className="h-4 w-4 text-green-600" />
                  <span className="font-medium">AES-256</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Access Controls</span>
                <div className="flex items-center space-x-1">
                  <Shield className="h-4 w-4 text-green-600" />
                  <span className="font-medium">Active</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Infrastructure Highlights */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Infrastructure Highlights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <h5 className="font-medium text-slate-700">Data Centers</h5>
            <div className="text-sm text-slate-600 space-y-1">
              <div>• 3 Primary data centers</div>
              <div>• 99.99% uptime guarantee</div>
              <div>• Global CDN distribution</div>
            </div>
          </div>
          <div className="space-y-2">
            <h5 className="font-medium text-slate-700">Security</h5>
            <div className="text-sm text-slate-600 space-y-1">
              <div>• End-to-end encryption</div>
              <div>• HIPAA compliant</div>
              <div>• SOC 2 Type II certified</div>
            </div>
          </div>
          <div className="space-y-2">
            <h5 className="font-medium text-slate-700">Performance</h5>
            <div className="text-sm text-slate-600 space-y-1">
              <div>• Sub-100ms response times</div>
              <div>• Auto-scaling infrastructure</div>
              <div>• Real-time data synchronization</div>
            </div>
          </div>
        </div>
      </div>

      {/* Support Information */}
      <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Technical Support</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="font-medium text-slate-700 mb-2">Contact Information</h5>
            <div className="text-sm text-slate-600 space-y-1">
              <div>• Email: support@zippyhealth.io</div>
              <div>• Phone: 1-800-ZIPPY-HEALTH</div>
              <div>• Response Time: &lt; 2 hours</div>
            </div>
          </div>
          <div>
            <h5 className="font-medium text-slate-700 mb-2">Documentation</h5>
            <div className="text-sm text-slate-600 space-y-1">
              <div>• API Documentation</div>
              <div>• Integration Guides</div>
              <div>• Troubleshooting Resources</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 